import { Typography } from '@eventbrite/marmalade';
import clsx from 'classnames';
import React, { useEffect, useMemo, useRef } from 'react';
import { useListNavigation } from './useListNavigation';
import type { TimeConfig } from './useTime';
import { useTime } from './useTime';

import styles from './Timepicker.module.scss';

export enum TimepickerTestIds {
    Container = 'timepickerContainer',
    Option = 'timepickerOption',
    Button = 'timepickerButton',
}

interface TimepickerProps extends TimeConfig {
    onTimeChange: (t: string) => void;
    selectedTime?: string;
    filter?: string;
}

const onBlurTrap = (e: React.MouseEvent) => e.preventDefault();

export const Timepicker: React.FC<TimepickerProps> = ({
    onTimeChange,
    selectedTime,
    interval = 30,
    locale,
    minTime,
    maxTime,
    filter,
}) => {
    const listRef = useRef<HTMLUListElement>(null);
    const { time } = useTime({
        interval,
        locale,
        minTime,
        maxTime,
    });

    const hours = useMemo(() => {
        if (!filter) {
            return time;
        }

        const query = filter.replace(' ', '').toLocaleLowerCase();
        const [hour, rest] = query.split(':');

        return time.filter((t) => {
            const label = t.label.replace(' ', '').toLocaleLowerCase();
            if (hour && rest) {
                const [h] = label.split(':');
                return h.includes(hour);
            }

            return label.includes(query);
        });
    }, [filter, time]);

    const listNavigation = useListNavigation({
        list: hours,
    });

    const hotkeyshandler = useMemo(
        () => ({
            ArrowDown: listNavigation.next,
            ArrowUp: listNavigation.prev,
            Enter: () =>
                listNavigation.selected >= 0 &&
                onTimeChange(hours[listNavigation.selected].value),
        }),
        [
            listNavigation.next,
            listNavigation.prev,
            listNavigation.selected,
            hours,
            onTimeChange,
        ],
    );

    useEffect(() => {
        const child = listRef.current?.children[listNavigation.selected];
        child?.scrollIntoView({ block: 'nearest' });
    }, [listNavigation.selected]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent): void => {
            const { key } = event;
            if (key in hotkeyshandler) {
                event.preventDefault();
                const handler =
                    hotkeyshandler[key as keyof typeof hotkeyshandler];
                handler();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [hotkeyshandler]);

    return (
        <ul
            ref={listRef}
            className={styles.timepicker}
            data-testid={TimepickerTestIds.Container}
        >
            {hours.map((hour, index) => {
                const isHovered = index === listNavigation.selected;

                return (
                    <li
                        key={hour.value}
                        data-time={hour.value}
                        data-selected={hour.value === selectedTime}
                        data-hovered={isHovered}
                        data-testid={TimepickerTestIds.Option}
                        className={clsx(styles.option, {
                            [styles.selected]: hour.value === selectedTime,
                            [styles.hover]: isHovered,
                        })}
                        onMouseOver={() =>
                            listNavigation.update({ cursor: index })
                        }
                    >
                        <button
                            data-testid={TimepickerTestIds.Button}
                            type="button"
                            data-time={hour.value}
                            className={styles.button}
                            aria-pressed={hour.value === selectedTime}
                            onClick={() => onTimeChange(hour.value)}
                            onMouseDown={onBlurTrap}
                        >
                            <time>
                                <Typography as="span" variant="body-md">
                                    {hour.label}
                                </Typography>
                            </time>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
};
